import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveRouteEntry } from './resolveRouteEntry';
import { TabPaneContext } from './TabPaneContext';
import { PaneRouteContent } from './PaneRouteContent';
import { getRetainedVisibilityStyle } from './retainedVisibility';
import { useDeferredEffectActive } from './useDeferredEffectActive';

interface KeepAlivePaneProps {
  path: string;
  isActive: boolean;
  notFoundElement: React.ReactNode;
}

export const KeepAlivePane: React.FC<KeepAlivePaneProps> = ({
  path,
  isActive,
  notFoundElement,
}) => {
  const location = useLocation();
  const resolved = useMemo(() => resolveRouteEntry(path), [path]);
  // Paint immediately; resume timers/effects after the transition.
  const isEffectActive = useDeferredEffectActive(isActive);

  const [lastSearch, setLastSearch] = useState('');
  const [lastHash, setLastHash] = useState('');

  useEffect(() => {
    if (isActive) {
      setLastSearch(location.search);
      setLastHash(location.hash);
    }
  }, [isActive, location.search, location.hash]);

  const frozenLocation = useMemo(
    () => ({
      pathname: path,
      search: isActive ? location.search : lastSearch,
      hash: isActive ? location.hash : lastHash,
      state: null,
      key: path,
    }),
    [path, isActive, location.search, location.hash, lastSearch, lastHash],
  );

  const paneContextValue = useMemo(
    () => ({ panePath: path, isPaneActive: isEffectActive }),
    [path, isEffectActive],
  );

  const [hasBeenActive, setHasBeenActive] = useState(isActive);

  useEffect(() => {
    if (isActive) {
      setHasBeenActive(true);
    }
  }, [isActive]);

  if (!resolved) {
    return isActive ? <>{notFoundElement}</> : null;
  }

  if (!hasBeenActive) {
    return null;
  }

  return (
    <div
      data-tab-keep-alive-pane={path}
      data-tab-keep-alive-active={isActive}
      style={getRetainedVisibilityStyle(isActive)}
      aria-hidden={!isActive}
    >
      <TabPaneContext.Provider value={paneContextValue}>
        <PaneRouteContent
          panePath={path}
          definition={resolved.definition}
          frozenLocation={frozenLocation}
        />
      </TabPaneContext.Provider>
    </div>
  );
};
